import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { MouseEvent, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { SidePanelProfile } from 'src/graphql/entities/side-panel-personalization';

interface ProfileMenuProps {
  profiles: SidePanelProfile[];
  activeProfileId: string | null;
  isDraft: boolean;
  disabled: boolean;
  onSelect: (profileId: string) => void;
  onDelete: (profileId: string) => void;
  onSaveAs: () => void;
}

const PROFILE_SOURCE_META: Record<
  SidePanelProfile['source'],
  { icon: string; hint: string | null }
> = {
  builtin: { icon: 'layout-dashboard', hint: 'The standard ProVision layout.' },
  group: { icon: 'users', hint: 'Assigned to your group' },
  user: { icon: 'bookmark', hint: null },
};

const menuItemSx = {
  borderRadius: 1.5,
  mx: 1,
  my: 0.25,
  alignItems: 'center',
  '&.Mui-selected': {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': { backgroundColor: 'primary.dark' },
    '& .MuiListItemIcon-root': { color: 'inherit' },
    '& .MuiListItemText-secondary': { color: 'inherit', opacity: 0.85 },
  },
} as const;

const ActiveCheck = () => (
  <DynamicIcon name="check" library="lucide" size={18} sx={{ ml: 1, flexShrink: 0 }} />
);

export const ProfileMenu = ({
  profiles,
  activeProfileId,
  isDraft,
  disabled,
  onSelect,
  onDelete,
  onSaveAs,
}: ProfileMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const close = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title="View profiles">
        {/* span keeps the tooltip working while the button is disabled */}
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            disabled={disabled}
            onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
            aria-label="View profiles"
            aria-expanded={open}
          >
            <DynamicIcon name="more-horizontal" library="lucide" size={20} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '90vw' } } }}
      >
        {isDraft ? (
          <MenuItem disabled selected sx={menuItemSx}>
            <ListItemIcon>
              <DynamicIcon name="pencil" library="lucide" size={20} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" fontWeight={600}>
                  Custom
                </Typography>
              }
              secondary={activeProfileId ? 'Unsaved changes' : 'Unsaved view'}
            />
            <ActiveCheck />
          </MenuItem>
        ) : null}
        {profiles.map((profile) => {
          const isActive = !isDraft && profile.id === activeProfileId;
          const meta = PROFILE_SOURCE_META[profile.source];

          return (
            <MenuItem
              key={profile.id}
              selected={isActive}
              onClick={() => {
                onSelect(profile.id);
                close();
              }}
              sx={menuItemSx}
            >
              <ListItemIcon sx={{ color: 'text.secondary' }}>
                <DynamicIcon name={meta.icon} library="lucide" size={20} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={600}>
                    {profile.name}
                  </Typography>
                }
                secondary={meta.hint}
              />
              {isActive ? <ActiveCheck /> : null}
              {profile.source === 'user' ? (
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={`Delete profile ${profile.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(profile.id);
                    close();
                  }}
                  sx={{ ml: 1, p: 0.25 }}
                >
                  <DynamicIcon name="trash-2" library="lucide" size={18} />
                </IconButton>
              ) : null}
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          onClick={() => {
            close();
            onSaveAs();
          }}
          sx={{ ...menuItemSx, color: 'primary.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DynamicIcon name="bookmark-plus" library="lucide" size={20} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="body2" fontWeight={600}>
                Save current as profile…
              </Typography>
            }
          />
        </MenuItem>
      </Menu>
    </>
  );
};
